import React, { useState, useEffect, lazy, Suspense } from 'react';
import axios from 'axios';
import styled, { createGlobalStyle } from 'styled-components';
import { Helmet } from 'react-helmet';
import { CopyToClipboard } from 'react-copy-to-clipboard';

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=VT323&display=swap');
  
  body {
    background-color: #5C94FC;
    color: #2C2C2C;
    font-family: 'VT323', monospace;
    font-size: 18px;
    overflow-x: hidden;
  }
`;

const AppContainer = styled.main`
  max-width: 800px;
  margin: 40px auto;
  padding: 30px;
  background-color: #FFF1E8;
  border: 4px solid #FFA300;
  box-shadow: 0 0 0 4px #2C2C2C, 0 0 0 8px #FFF1E8;
  image-rendering: pixelated;
  border-radius: 20px;
`;

const Title = styled.h1`
  color: #FF004D;
  text-align: center;
  font-size: 2.5em;
  margin-bottom: 30px;
  text-shadow: 2px 2px #FFA300;
`;

const Button = styled.button`
  background-color: #00E436;
  color: #2C2C2C;
  border: none;
  padding: 12px 24px;
  cursor: pointer;
  font-size: 20px;
  font-family: 'VT323', monospace;
  display: block;
  margin: 0 auto;
  image-rendering: pixelated;
  border: 2px solid #2C2C2C;
  box-shadow: 2px 2px #2C2C2C;

  &:hover {
    background-color: #29ADFF;
    color: #FFF1E8;
  }
`;

const CopyButton = styled(Button)`
  font-size: 14px;
  padding: 8px 16px;
  margin-top: 10px;
`;

const EmailDisplay = styled.section`
  background-color: #C2C3C7;
  padding: 15px;
  text-align: center;
  font-size: 1.2em;
  margin-bottom: 20px;
  word-break: break-all;
  border: 2px solid #2C2C2C;
`;

const Timer = styled.p`
  font-size: 1.5em;
  text-align: center;
  margin-bottom: 30px;
  color: #FF004D;
`;

const MessageList = styled.ul`
  list-style-type: none;
  padding: 0;
`;

const MessageItem = styled.li`
  background-color: #FFF1E8;
  margin: 15px 0;
  padding: 20px;
  border: 2px solid #FFA300;

  &:hover {
    background-color: #FFEC27;
  }
`;

const NoMessages = styled.p`
  text-align: center;
  font-style: italic;
  color: #83769C;
`;

const InfoPanel = styled.aside`
  background-color: #FFCCAA;
  padding: 20px;
  margin-top: 30px;
  border: 2px solid #FF77A8;
`;

const InfoTitle = styled.h3`
  color: #FF004D;
  text-align: center;
  margin-bottom: 15px;
`;

const InfoText = styled.p`
  color: #2C2C2C;
  text-align: center;
  margin-bottom: 10px;
  font-size: 1em;
`;

const LinkContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 20px;
`;

const SupportLink = styled.a`
  background-color: #FFA300;
  color: #2C2C2C;
  text-decoration: none;
  padding: 10px 20px;
  font-weight: bold;
  font-size: 1em;
  border: 2px solid #2C2C2C;
  box-shadow: 2px 2px #2C2C2C;

  &:hover {
    background-color: #FF77A8;
    color: #FFF1E8;
  }
`;

const LazyMessageList = lazy(() => import('./MessageList'));

const ErrorMessage = styled.div`
  background-color: #FF77A8;
  color: #FFF1E8;
  padding: 10px;
  margin: 10px 0;
  border: 2px solid #FF004D;
  text-align: center;
`;

const LoadingIndicator = styled.div`
  text-align: center;
  color: #FF004D;
  font-size: 1.2em;
  margin: 20px 0;
`;

const Spinner = styled.div`
  border: 4px solid #FFA300;
  border-top: 4px solid #FF004D;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 20px auto;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const CheckingIndicator = styled.div`
  text-align: center;
  color: #83769C;
  font-size: 0.9em;
  margin-top: 10px;
`;

function App() {
  const [email, setEmail] = useState('');
  const [inboxId, setInboxId] = useState('');
  const [messages, setMessages] = useState([]);
  const [timeLeft, setTimeLeft] = useState(600);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [isCreatingEmail, setIsCreatingEmail] = useState(false);
  const [isFetchingEmails, setIsFetchingEmails] = useState(false);

  useEffect(() => {
    let timer;
    if (email && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prevTime) => prevTime - 1);
        checkEmails();
      }, 1000);
    } else if (timeLeft === 0) {
      setEmail('');
      setInboxId('');
      setMessages([]);
    }
    return () => clearInterval(timer);
  }, [email, timeLeft, inboxId]);

  const createEmail = async () => {
    try {
      setError(null);
      setIsCreatingEmail(true);
      const response = await axios.post('http://localhost:5000/create-email');
      setEmail(response.data.email);
      setInboxId(response.data.id);
      setTimeLeft(600);
    } catch (error) {
      console.error('Error creating email:', error);
      setError('Failed to create email. Please try again.');
    } finally {
      setIsCreatingEmail(false);
    }
  };

  const checkEmails = async () => {
    if (inboxId) {
      try {
        const response = await axios.get(`http://localhost:5000/check-emails?id=${inboxId}`);
        if (response.data.length > messages.length) {
          setIsFetchingEmails(true);
          setMessages(response.data);
        }
      } catch (error) {
        console.error('Error checking emails:', error);
      } finally {
        setIsFetchingEmails(false);
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>10-Minute Anonymous Email - Temporary Disposable Email Service</title>
        <meta name="description" content="Generate a temporary, anonymous email address that lasts for 10 minutes. Perfect for quick signups and testing without using your personal email." />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "WebApplication",
              "name": "10-Minute Email",
              "description": "Generate a temporary, anonymous email address that lasts for 10 minutes.",
              "url": "https://your-domain.com",
              "applicationCategory": "UtilityApplication",
              "operatingSystem": "All",
              "offers": {
                "@type": "Offer",
                "price": "0"
              },
              "creator": {
                "@type": "Person",
                "name": "Leviathan McLennan"
              }
            }
          `}
        </script>
      </Helmet>
      <GlobalStyle />
      <AppContainer>
        <header>
          <Title>10-Minute Anonymous Email</Title>
        </header>
        {!email ? (
          <>
            <Button onClick={createEmail} disabled={isCreatingEmail}>
              {isCreatingEmail ? 'Generating...' : 'Generate Temporary Email'}
            </Button>
            {isCreatingEmail && (
              <>
                <Spinner />
                <LoadingIndicator>Creating your temporary email...</LoadingIndicator>
              </>
            )}
          </>
        ) : (
          <>
            {error && <ErrorMessage>{error}</ErrorMessage>}
            <EmailDisplay>
              {email}
              <CopyToClipboard text={email} onCopy={() => setCopied(true)}>
                <CopyButton>{copied ? 'Copied!' : 'Copy Email'}</CopyButton>
              </CopyToClipboard>
            </EmailDisplay>
            <Timer>Time remaining: {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}</Timer>
            <section>
              <h3>Received Messages:</h3>
              {isFetchingEmails && messages.length > 0 ? (
                <>
                  <Spinner />
                  <LoadingIndicator>Fetching new messages...</LoadingIndicator>
                </>
              ) : (
                <Suspense fallback={<LoadingIndicator>Loading messages...</LoadingIndicator>}>
                  <LazyMessageList messages={messages} />
                </Suspense>
              )}
            </section>
          </>
        )}
        
        <aside>
          <InfoPanel>
            <InfoTitle>About This Service</InfoTitle>
            <InfoText>
              Remain anonymous and keep your inbox clean with a disposable email address that lasts for 10 minutes.
              It's perfect for quick signups or testing without using your personal email.
            </InfoText>
            <InfoText>
              Made by Leviathan. If you find this service useful, consider supporting me below!:
            </InfoText>
            <LinkContainer>
              <SupportLink href="https://www.patreon.com/your_patreon" target="_blank" rel="noopener noreferrer">
                Support on Patreon
              </SupportLink>
              <SupportLink href="https://ko-fi.com/your_kofi" target="_blank" rel="noopener noreferrer">
                Buy me a Coffee
              </SupportLink>
            </LinkContainer>
          </InfoPanel>
        </aside>
      </AppContainer>
    </>
  );
}

export default App;